import React, { useCallback, useEffect, useState } from 'react';
import { MdAdd, MdClose, MdDeveloperBoard, MdInventory2 } from 'react-icons/md';
import { StatusPill, fetchJson, worst } from './deviceHealth';

// Hardware catalog (Admin → Monitoring → Hardware catalog) and the per-device
// "Installed hardware" list. The backend validates everything
// (backend/app/services/device_hardware.py); see docs/architecture/monitoring.

export function useCatalog() {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState('');
  const reload = useCallback(() => fetchJson('/admin/hardware-catalog').then(setCatalog).catch(err => setError(err.message)), []);
  useEffect(() => { reload(); }, [reload]);
  return { catalog, error, reload };
}

function partStatus(device, model, savedIds) {
  if (!savedIds.includes(model.id)) return <StatusPill status="neutral">Save to check</StatusPill>;
  const checks = Object.values(device.components || {}).filter(c => c.part === model.key);
  if (!checks.length) return <StatusPill status="neutral">{device.report ? 'Checking…' : 'No report yet'}</StatusPill>;
  const result = worst(checks);
  return <StatusPill status={result.status}>{result.label}</StatusPill>;
}

export function InstalledHardware({ device, onSaved }) {
  const { catalog, error: loadError } = useCatalog();
  const savedIds = device.hardware_selection?.model_ids || [];
  const confirmed = device.hardware_selection?.confirmed;
  const [ids, setIds] = useState(savedIds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  if (loadError) return <article className="dm-card"><div className="dm-error" role="alert">{loadError}</div></article>;
  if (!catalog) return <article className="dm-card"><p className="dm-muted">Loading hardware…</p></article>;

  const change = next => { setSaved(false); setError(''); setIds(next); };
  const save = async () => {
    setSaving(true);
    try {
      await fetchJson(`/admin/devices/${device.device_id}/hardware`, 'PUT', { model_ids: ids });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return <article className="dm-card dm-installed" aria-label="Installed hardware">
    <div className="dm-chart-top">
      <h3><MdDeveloperBoard aria-hidden="true" size={18} />Installed hardware</h3>
      <button type="button" className="dm-button" onClick={save} disabled={saving}>{saving ? 'Saving…' : confirmed ? 'Save' : 'Confirm hardware'}</button>
    </div>
    {!confirmed && <p className="dm-banner">These are the catalog defaults. Check what is built into this device and confirm.</p>}
    {error && <div className="dm-error" role="alert">{error}</div>}
    {saved && !error && <p className="dm-note">Saved. The device checks the new selection within a minute.</p>}
    <div className="dm-hw-rows">
      {catalog.categories.filter(category => category.active).map(category => {
        const models = catalog.models.filter(model => model.category_id === category.id);
        const chosen = models.filter(model => ids.includes(model.id));
        const available = models.filter(model => model.active && !ids.includes(model.id));
        return <div className="dm-hw-row" key={category.id}>
          <span className="dm-hw-category">{category.label}</span>
          <div className="dm-hw-parts">
            {chosen.length === 0 && <span className="dm-muted">Not installed</span>}
            {chosen.map(model => <div className="dm-hw-part" key={model.id}>
              <span>{model.label}</span>
              {partStatus(device, model, savedIds)}
              <button type="button" className="dm-icon-button" aria-label={`Remove ${model.label}`} onClick={() => change(ids.filter(id => id !== model.id))}><MdClose size={16} /></button>
            </div>)}
          </div>
          {available.length > 0 && <select aria-label={`Add ${category.label}`} value="" onChange={event => change([...ids, Number(event.target.value)])}>
            <option value="">Add…</option>
            {available.map(model => <option key={model.id} value={model.id}>{model.label}</option>)}
          </select>}
        </div>;
      })}
    </div>
  </article>;
}

function InlineName({ value, label, onSave }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => { if (text.trim() && text.trim() !== value) onSave(text.trim()); else setText(value); };
  return <input className="dm-inline" aria-label={label} value={text} onChange={e => setText(e.target.value)} onBlur={commit}
    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setText(value); e.currentTarget.blur(); } }} />;
}

const emptyForm = { category_id: '', label: '', probe: '', params: {}, is_default: false };

function ModelForm({ catalog, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const schema = catalog.probes[form.probe]?.params || {};
  const update = change => { setError(''); setForm({ ...form, ...change }); };

  const submit = async event => {
    event.preventDefault();
    if (!form.category_id || !form.label.trim() || !form.probe) {
      setError('Choose a category, a name and how it is checked.');
      return;
    }
    try {
      await fetchJson('/admin/hardware-catalog', 'POST', { ...form, category_id: Number(form.category_id) });
      setForm(emptyForm);
      onCreated();
    } catch (err) {
      setError(err.message);
    }
  };

  return <form className="dm-card dm-form" onSubmit={submit} aria-label="Add hardware model">
    <h3><MdAdd aria-hidden="true" size={18} />Add model</h3>
    <div className="dm-form-grid">
      <label>Category<select value={form.category_id} onChange={e => update({ category_id: e.target.value })}>
        <option value="">Choose…</option>{catalog.categories.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select></label>
      <label>Name<input value={form.label} placeholder="Visaton FRS 7" onChange={e => update({ label: e.target.value })} /></label>
      <label>How it is checked<select value={form.probe} onChange={e => update({
        probe: e.target.value, params: Object.fromEntries(Object.keys(catalog.probes[e.target.value]?.params || {}).map(name => [name, ''])),
      })}><option value="">Choose…</option>{Object.entries(catalog.probes).map(([name, probe]) => <option key={name} value={name}>{probe.label}</option>)}</select></label>
      {Object.entries(schema).map(([name, param]) => <label key={name}>{param.label}
        <input value={form.params[name] ?? ''} placeholder={param.hint} onChange={e => update({ params: { ...form.params, [name]: e.target.value } })} /></label>)}
    </div>
    <label className="dm-check"><input type="checkbox" checked={form.is_default} onChange={e => update({ is_default: e.target.checked })} />Installed by default in new devices</label>
    {error && <p className="dm-field-error" role="alert">{error}</p>}
    <button type="submit" className="dm-button">Add model</button>
    <p className="dm-note">A new kind of check (not in the list) needs a small code change on the device; see docs/architecture/monitoring.</p>
  </form>;
}

function CategoryForm({ onCreated, onError }) {
  const [label, setLabel] = useState('');
  const submit = async event => {
    event.preventDefault();
    if (!label.trim()) return onError('Enter a category name.');
    try {
      await fetchJson('/admin/hardware-categories', 'POST', { label });
      setLabel('');
      onCreated();
    } catch (err) {
      onError(err.message);
    }
  };
  return <form className="dm-category-form" onSubmit={submit} aria-label="Add category">
    <input value={label} placeholder="New category, e.g. Distance sensors" aria-label="New category name" onChange={e => setLabel(e.target.value)} />
    <button type="submit" className="dm-button"><MdAdd aria-hidden="true" size={16} />Add category</button>
  </form>;
}

export default function HardwareCatalog() {
  const { catalog, error, reload } = useCatalog();
  const [rowError, setRowError] = useState('');
  if (error) return <div className="dm-error" role="alert">{error}</div>;
  if (!catalog) return <p className="dm-muted">Loading catalog…</p>;

  const patch = async (path, change) => {
    try {
      await fetchJson(path, 'PATCH', change);
      setRowError('');
      reload();
    } catch (err) {
      setRowError(err.message);
    }
  };

  return <section aria-label="Hardware catalog">
    <p className="dm-muted dm-intro">Categories and parts a device can be built with. Choose them per device under Installed hardware; the device then checks exactly those parts.</p>
    {rowError && <div className="dm-error" role="alert">{rowError}</div>}
    <CategoryForm onCreated={() => { setRowError(''); reload(); }} onError={setRowError} />
    {catalog.categories.map(category => <article className={`dm-card dm-catalog${category.active ? '' : ' is-removed'}`} key={category.id} aria-label={`Category ${category.label}`}>
      <div className="dm-chart-top">
        <h3><MdInventory2 aria-hidden="true" size={18} />
          <InlineName value={category.label} label={`Category name ${category.label}`} onSave={label => patch(`/admin/hardware-categories/${category.id}`, { label })} />
        </h3>
        <button type="button" className="dm-button" onClick={() => patch(`/admin/hardware-categories/${category.id}`, { active: !category.active })}>
          {category.active ? 'Remove category' : 'Restore category'}</button>
      </div>
      {!category.active && <p className="dm-note">Removed: devices no longer check or show these parts.</p>}
      <table className="dm-mini"><thead><tr><th>Model</th><th>Checked by</th><th>Settings</th><th>Default</th><th>Active</th></tr></thead>
        <tbody>{catalog.models.filter(model => model.category_id === category.id).map(model => <tr key={model.id} className={model.active ? undefined : 'is-inactive'}>
          <td><InlineName value={model.label} label={`Model name ${model.label}`} onSave={label => patch(`/admin/hardware-catalog/${model.id}`, { label })} /></td>
          <td>{catalog.probes[model.probe]?.label || model.probe}</td>
          <td>{Object.entries(model.params).map(([name, value]) => `${catalog.probes[model.probe]?.params[name]?.label || name}: ${value}`).join(' · ') || '—'}</td>
          <td><input type="checkbox" aria-label={`Default: ${model.label}`} checked={model.is_default} disabled={!model.active}
            onChange={e => patch(`/admin/hardware-catalog/${model.id}`, { is_default: e.target.checked })} /></td>
          <td><input type="checkbox" aria-label={`Active: ${model.label}`} checked={model.active} onChange={e => patch(`/admin/hardware-catalog/${model.id}`, { active: e.target.checked })} /></td>
        </tr>)}</tbody></table>
    </article>)}
    <ModelForm catalog={catalog} onCreated={() => { setRowError(''); reload(); }} />
  </section>;
}
